import { z } from 'zod'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'
import { useQueryRequest } from '~/ui/hooks/utils'

import { ExtendedBalanceSchema } from '../..'

const EarlyRedemptionSchema = z.object({
  available: z.boolean(),
  date: z.string().nullable()
})

const InvestmentBalanceSchema = z.object({
  grossAmount: z.number(),
  netAmount: z.number(),
  principal: z.number(),
  interest: z.number(),
  iof: z.number(),
  ir: z.number()
})

const InvestmentSchema = z.object({
  balance: InvestmentBalanceSchema,
  id: z.string(),
  investmentDate: z.string(),
  maturityDate: z.string()
})
export type MovementsInvestment = z.infer<typeof InvestmentSchema>

const MarketInvestedProductMovementSchema = z.object({
  accrualPeriodicity: z.string(),
  apy: z.string(),
  balance: ExtendedBalanceSchema,
  distributionPartner: z.string(),
  earlyRedemption: EarlyRedemptionSchema,
  firstInvestDate: z.string(),
  id: z.string(),
  investments: z.array(InvestmentSchema),
  name: z.string(),
  type: z.string()
})

export type MarketInvestedProductMovements = z.infer<typeof MarketInvestedProductMovementSchema>

async function fetchMarketInvestedProductsMovements({
  accountId,
  productId
}: {
  accountId: string
  productId: string
}) {
  const { data } = await httpClientBanking({
    url: `/investments/accounts/${accountId}/storefront/invested_products/${productId}/investments`,
    method: 'GET'
  })

  return MarketInvestedProductMovementSchema.parse(data)
}

export function getUseFetchMarketInvestedProductsMovementsKey({
  accountId,
  productId
}: {
  accountId: string
  productId: string
}) {
  return ['useFetchMarketInvestedProductsMovements', accountId, productId] as const
}

export function useFetchMarketInvestedProductsMovements({
  accountId,
  productId,
  options
}: {
  accountId: string
  productId: string
  options?: { suspense?: boolean }
}) {
  return useQueryRequest<MarketInvestedProductMovements, unknown, true>(
    [...getUseFetchMarketInvestedProductsMovementsKey({ accountId, productId })],
    () => fetchMarketInvestedProductsMovements({ accountId, productId }),
    {
      suspense: true,
      ...options
    }
  )
}
